import './manager-check-item-blocks.scss'
import { formatReferenceCopy } from 'utils/typography'
import {
  useState,
} from 'react'
import {
  useTranslation,
} from 'react-i18next'
import {
  Box,
  IconButton,
  Tooltip,
} from '@mui/material'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import {
  APPLICATION_CHECK_STATUS,
} from '../../../../../config/constants'

import {
  activeCheckItemKind,
  checkItemBlockNeedsClientAttention,
  showManagerCheckItemDiscussionComposer,
  showApplicationPreparedDocumentsZipDownload,
} from '../check-block-utils'
import CheckItemBlockCard from './check-item-block-card'
import { checkItemDiscussionHasContent } from './check-item-discussion-content'
import CheckItemCompleteBlockModal from './check-item-complete-block-modal'
import CheckItemDiscussion from './check-item-discussion'
import PreparedDocumentsZipButton from './prepared-documents-zip-button'
import TnvedCheckProductsPanel from './tnved-check-products-panel'
import {
  tnvedReviewsHaveCodeChanges,
} from '../tnved-review-utils'

const ManagerCheckItemBlocks = ({
  app,
  publicId,
  checkStatuses,
  runAction,
  onUpdateCheckItem,
  onPostCheckItemComment,
  onUploadDocument,
  onDeleteDocument,
}: ApplicationManagerCheckItemBlocksProps) => {
  const { t } = useTranslation()
  const showComposer = showManagerCheckItemDiscussionComposer(app)
  const [ completeItemKind, setCompleteItemKind ] = useState<string | null>(null)

  const completeItem = completeItemKind
    ? app.check_items.find((entry) => entry.kind === completeItemKind) ?? null
    : null
  const currentBlockKind = activeCheckItemKind(app.check_items)
  const showZipDownload = showApplicationPreparedDocumentsZipDownload({ app, isManager: true })

  return (
    <Box className='application-manager-check-item-blocks'>
      {showZipDownload && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <PreparedDocumentsZipButton app={app} publicId={publicId} isManager />
        </Box>
      )}

      <Box className='application-manager-check-item-blocks__check-blocks'>
        {app.check_items.map((item) => {
          const needsClientAttention = checkItemBlockNeedsClientAttention({ app: app, item: item })
          const isCompleted = item.status === APPLICATION_CHECK_STATUS.COMPLETED
          const tnvedCloseBlocked = item.kind === 'tnved_code'
            && tnvedReviewsHaveCodeChanges(item.tnved_reviews ?? [ ])
            && !item.tnved_changes_confirmed

          const showBlockActions = showComposer && !isCompleted
          const showTnvedPanel = item.kind === 'tnved_code' && Boolean(app.calculation_number)
          const hasDiscussion = checkItemDiscussionHasContent({
            app,
            item,
            showEvents: true,
            showComposer: showBlockActions,
          })
          const hasContent = showTnvedPanel || hasDiscussion

          return (
            <CheckItemBlockCard
              key={item.kind}
              managerView
              item={item}
              hasContent={hasContent}
              savedStatus={item.status}
              isActiveBlock={item.kind === currentBlockKind}
              needsClientAttention={needsClientAttention}
              completionAction={showBlockActions ? (
                <Tooltip title={formatReferenceCopy(t(tnvedCloseBlocked
                  ? 'applications__text__tnvedCloseBlockedHint'
                  : 'applications__text__blockCloseCheck'))}>
                  <Box component='span' sx={{
                    display: 'inline-flex',
                  }}>
                    <IconButton
                      size='small'
                      color='primary'
                      aria-label={formatReferenceCopy(t('applications__text__blockCompleteModalTitle', { block: item.kind_label }))}
                      disabled={tnvedCloseBlocked}
                      onClick={() => setCompleteItemKind(item.kind)}
                    >
                      <TaskAltIcon />
                    </IconButton>
                  </Box>
                </Tooltip>
              ) : undefined}
            >
              {showTnvedPanel && (
                <TnvedCheckProductsPanel
                  orderId={app.calculation_number}
                  managerView
                  reviews={item.tnved_reviews}
                  changesConfirmed={Boolean(item.tnved_changes_confirmed)}
                  runAction={runAction}
                  onSaveReviews={(tnved_reviews) => onUpdateCheckItem({
                    kind: item.kind,
                    body: { tnved_reviews },
                  })}
                />
              )}

              <CheckItemDiscussion
                managerView
                app={app}
                item={item}
                showEvents
                checkStatuses={checkStatuses}
                showComposer={showBlockActions}
                runAction={runAction}
                onPostComment={onPostCheckItemComment}
              />
            </CheckItemBlockCard>
          )
        })}
      </Box>

      <CheckItemCompleteBlockModal
        open={Boolean(completeItem)}
        item={completeItem}
        runAction={runAction}
        onClose={() => setCompleteItemKind(null)}
        onUploadDocument={onUploadDocument}
        onCompleteBlock={({ kind, manager_note }) => onUpdateCheckItem({
          kind,
          body: {
            status: APPLICATION_CHECK_STATUS.COMPLETED,
            manager_note,
          },
        })}
      />
    </Box>
  )
}

export default ManagerCheckItemBlocks
